import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloudPolicy, PolicyExecutionRecord, PolicyExecutionStep, PolicyFeasibility } from '../../types/policies';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../ui/common-ui/dialog';
import { Button } from '../../../../ui/common-ui/button';
import { Input } from '../../../../ui/common-ui/input';
import { Textarea } from '../../../../ui/common-ui/textarea';
import { Label } from '../../../../ui/common-ui/label';
import { Badge } from '../../../../ui/common-ui/badge';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Clock,
  Cloud,
  Database,
  Flame,
  Info,
  KeyRound,
  Link2,
  Loader2,
  OctagonAlert,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  User,
  Wrench,
  X,
} from 'lucide-react';
import { fetchPolicyExecutionTimeline, getApiErrorMessage, recreatePolicy, updatePolicy } from '../../services/api';
import { toast } from 'sonner';
import { normalizeCloudPolicy, normalizeExecutionHistory, normalizePolicySeverity } from '../../lib/policyTransforms';
import { filterResourcesByPolicyTargetTypes } from '../../lib/resourceTargeting';
import { formatRelativeTime } from './PolicyCard';
import {
  fetchAlertSelectableItems,
  fetchAlerts,
  SelectableItemsResponse,
} from '../../../CloudAlertManagement/services/api';
import './PolicyDetailsEdit.css';

interface PolicyDetailsEditProps {
  policy: CloudPolicy | null;
  open: boolean;
  onClose: () => void;
  tenantName?: string;
  cloudId?: string;
  isCreateMode?: boolean;
  onPolicyUpdate?: (policy: CloudPolicy) => void;
  onRefreshRequested?: () => void;
  linkedAlertDetails?: Record<string, any>;
}

type TargetKind = 'users' | 'nhis' | 'resources';
type DetailsTab = 'details' | 'timeline' | 'data';

type PickerItem = {
  id: string;
  label: string;
  sublabel?: string;
};

type AlertOption = {
  id: string;
  title: string;
  severity: string;
  permission: string;
  type?: string;
};

const RUN_POLL_INTERVAL_MS = 3000;
const CARD_SHADOW = 'shadow-[0_2px_10px_rgb(15_23_42/0.05)]';

const severityStyles: Record<string, { badge: string; panel: string; dot: string; active: string; icon: any }> = {
  Critical: {
    badge: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
    panel: 'border-l-rose-500',
    dot: 'bg-rose-500',
    active: 'border-rose-300 bg-rose-50 text-rose-700 shadow-sm',
    icon: Flame,
  },
  High: {
    badge: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    panel: 'border-l-orange-500',
    dot: 'bg-orange-500',
    active: 'border-orange-300 bg-orange-50 text-orange-700 shadow-sm',
    icon: AlertTriangle,
  },
  Medium: {
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    panel: 'border-l-amber-500',
    dot: 'bg-amber-500',
    active: 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm',
    icon: AlertCircle,
  },
  Low: {
    badge: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    panel: 'border-l-emerald-500',
    dot: 'bg-emerald-500',
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm',
    icon: Info,
  },
};

const severityOptions = ['Critical', 'High', 'Medium', 'Low'];

const targetConfig: Record<TargetKind, { title: string; icon: any; accent: string; empty: string }> = {
  users: {
    title: 'Targeted Users',
    icon: User,
    accent: 'text-blue-600 bg-blue-50 border-blue-200',
    empty: 'No users returned for this cloud context.',
  },
  nhis: {
    title: 'Targeted Service Accounts',
    icon: KeyRound,
    accent: 'text-violet-600 bg-violet-50 border-violet-200',
    empty: 'No service accounts, roles, secrets, or tokens returned.',
  },
  resources: {
    title: 'Targeted Resources',
    icon: Database,
    accent: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    empty: 'No resources returned for this cloud context.',
  },
};

const visibleTargets: TargetKind[] = ['users', 'nhis', 'resources'];

const runModeLabels: Record<string, string> = {
  ENABLE: 'Enforcement run',
  DISABLE: 'Rollback run',
  REMEDIATE: 'Remediation run',
};

const runToneStyles = {
  SUCCESS: {
    label: 'Succeeded',
    icon: CircleCheck,
    border: 'border-emerald-500/30 border-l-emerald-500',
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    text: 'text-emerald-700',
  },
  FAILED: {
    label: 'Failed',
    icon: OctagonAlert,
    border: 'border-red-500/30 border-l-red-500',
    badge: 'border-red-200 bg-red-50 text-red-700',
    text: 'text-red-700',
  },
  RUNNING: {
    label: 'Running',
    icon: Loader2,
    border: 'border-primary/30 border-l-primary',
    badge: 'border-primary/30 bg-primary/5 text-primary',
    text: 'text-primary',
  },
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map(item => String(item)).filter(Boolean);
};

const hasAllTargets = (value: string[]) => value.some(item => item === '' || item === '*');

const toggleValue = (list: string[], value: string) => (
  list.includes(value) ? list.filter(item => item !== value) : [...list, value]
);

const extractPermission = (steps: any): string => {
  const values = steps && typeof steps === 'object' ? Object.values(steps) : [];
  for (const step of values) {
    if (typeof step === 'string') return step;
    if (step && typeof step === 'object') {
      const stepData = step as Record<string, any>;
      return stepData.action || stepData.operation || stepData.eventName || stepData.eventSource || Object.keys(stepData.conditions || {})[0] || '';
    }
  }
  return '';
};

const flattenAlerts = (grouped: any): AlertOption[] => {
  const alerts: AlertOption[] = [];
  Object.entries(grouped || {}).forEach(([type, severityGroups]) => {
    Object.values((severityGroups || {}) as Record<string, any>).forEach((items: any) => {
      if (!Array.isArray(items)) return;
      items.forEach((alert: any) => {
        const id = String(alert.alertId || alert.id || '');
        if (!id) return;
        alerts.push({
          id,
          title: alert.title || 'Untitled alert',
          severity: normalizePolicySeverity(alert.severity),
          permission: extractPermission(alert.steps),
          type,
        });
      });
    });
  });
  return alerts;
};

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const triggerLabel = (run: PolicyExecutionRecord) => {
  if (run.trigger?.type !== 'ALERT') return 'Run manually';
  const alert = run.trigger.alerts?.[0];
  if (!alert) return 'Triggered by an alert';
  const extra = (run.trigger.alerts?.length || 0) > 1 ? ` and ${(run.trigger.alerts?.length || 1) - 1} more` : '';
  return `Triggered by "${alert.title}"${alert.actor ? ` (${alert.actor})` : ''}${extra}`;
};

function SeverityEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-1 ${CARD_SHADOW}`}>
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
        {severityOptions.map(option => {
          const style = severityStyles[option] || severityStyles.Medium;
          const Icon = style.icon;
          const selected = normalizePolicySeverity(value) === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition ${
                selected
                  ? style.active
                  : 'border-transparent bg-transparent text-slate-500 hover:border-slate-200 hover:text-slate-800'
              }`}
            >
              {selected ? <Check className="h-3.5 w-3.5" /> : <Icon className={`h-3.5 w-3.5 ${style.badge.split(' ').slice(1, 2).join(' ')}`} />}
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeading({ icon: Icon, accent, title, subtitle }: { icon: any; accent: string; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${accent}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
  );
}

function TargetPicker({
  kind,
  items,
  included,
  excluded,
  onIncludedChange,
  onExcludedChange,
}: {
  kind: TargetKind;
  items: PickerItem[];
  included: string[];
  excluded: string[];
  onIncludedChange: (next: string[]) => void;
  onExcludedChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const config = targetConfig[kind];

  const filtered = items.filter(item => `${item.label} ${item.sublabel || ''}`.toLowerCase().includes(query.toLowerCase()));
  const allIncluded = hasAllTargets(included);
  const allExcluded = hasAllTargets(excluded);
  const allItemIds = items.map(item => item.id);
  const hasItems = allItemIds.length > 0;
  const isEveryItemIncluded = allIncluded && excluded.length === 0;
  const isEveryItemExcluded = allExcluded && included.length === 0;
  const includedCount = allIncluded
    ? items.length - items.filter(item => excluded.includes(item.id)).length
    : included.filter(id => id !== '*' && id !== '').length;
  const excludedCount = allExcluded
    ? items.length - items.filter(item => included.includes(item.id)).length
    : excluded.filter(id => id !== '*' && id !== '').length;

  return (
    <section className={`flex flex-col rounded-2xl border border-border bg-card p-4 ${CARD_SHADOW}`}>
      <div
        className="flex cursor-pointer select-none flex-col gap-3 rounded-md sm:flex-row sm:items-center sm:justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <SectionHeading
          icon={config.icon}
          accent={config.accent}
          title={config.title}
          subtitle={`${includedCount} included, ${excludedCount} excluded`}
        />
        <div className="flex w-full flex-wrap items-center gap-2 pl-10 sm:w-auto sm:pl-0">
          <Button
            type="button"
            size="sm"
            variant={isEveryItemIncluded ? 'default' : 'outline'}
            disabled={!hasItems}
            className="h-8 rounded-md px-2.5 text-xs"
            onClick={(event) => {
              event.stopPropagation();
              onIncludedChange(allItemIds);
              onExcludedChange([]);
            }}
          >
            {isEveryItemIncluded ? <Check className="mr-1 h-3 w-3" /> : null}
            Include all
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isEveryItemExcluded ? 'destructive' : 'outline'}
            disabled={!hasItems}
            className="h-8 rounded-md px-2.5 text-xs"
            onClick={(event) => {
              event.stopPropagation();
              onIncludedChange([]);
              onExcludedChange(allItemIds);
            }}
          >
            {isEveryItemExcluded ? <Check className="mr-1 h-3 w-3" /> : null}
            Exclude all
          </Button>
          <div className={`ml-auto p-1 text-slate-400 transition-transform sm:ml-0 ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 flex max-h-[400px] min-h-0 flex-col">
          <div className="relative mb-3 shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={`Search ${config.title.toLowerCase()}...`}
              className="h-10 rounded-md border-border/70 bg-card pl-9"
              onClick={event => event.stopPropagation()}
            />
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pb-2 pr-1">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card p-4 text-sm text-slate-500">
                {config.empty}
              </div>
            ) : (
              filtered.map(item => {
                const isExplicitlyExcluded = excluded.includes(item.id);
                const isExplicitlyIncluded = included.includes(item.id);
                const isExcluded = allExcluded ? !isExplicitlyIncluded : isExplicitlyExcluded;
                const isIncluded = allIncluded ? !isExplicitlyExcluded : isExplicitlyIncluded;
                const toggleIncluded = () => {
                  if (allIncluded) {
                    if (isIncluded) onExcludedChange([...excluded, item.id]);
                    else onExcludedChange(excluded.filter(id => id !== item.id));
                  } else if (allExcluded) {
                    if (isIncluded) onIncludedChange(included.filter(id => id !== item.id));
                    else onIncludedChange([...included, item.id]);
                  } else {
                    onIncludedChange(toggleValue(included, item.id));
                    if (!isIncluded && isExcluded) onExcludedChange(excluded.filter(value => value !== item.id));
                  }
                };
                const toggleExcluded = () => {
                  if (allIncluded) {
                    if (isExcluded) onExcludedChange(excluded.filter(id => id !== item.id));
                    else onExcludedChange([...excluded, item.id]);
                  } else if (allExcluded) {
                    if (isExcluded) onIncludedChange([...included, item.id]);
                    else onIncludedChange(included.filter(id => id !== item.id));
                  } else {
                    onExcludedChange(toggleValue(excluded, item.id));
                    if (!isExcluded && isIncluded) onIncludedChange(included.filter(value => value !== item.id));
                  }
                };

                return (
                  <div
                    key={item.id}
                    className={`grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl border px-3 py-2 shadow-[0_1px_3px_rgb(15_23_42/0.04)] transition-colors sm:grid-cols-[auto_minmax(0,1fr)_auto] ${
                      isExcluded ? 'border-rose-300 bg-rose-50/70' : isIncluded ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleIncluded();
                      }}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
                        isIncluded ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-slate-300 bg-white text-transparent hover:border-primary/50'
                      }`}
                      aria-label={isIncluded ? `Unselect ${item.label}` : `Select ${item.label}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleIncluded();
                      }}
                      className="block w-full min-w-0 text-left"
                    >
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{item.label}</p>
                      {item.sublabel && <p className="truncate text-xs text-slate-500">{item.sublabel}</p>}
                    </button>
                    <div className="col-span-2 flex min-w-0 flex-wrap items-center justify-end gap-1.5 sm:col-span-1 sm:flex-nowrap">
                      <Button
                        type="button"
                        size="sm"
                        variant={isIncluded ? 'default' : 'outline'}
                        className="h-7 w-7 shrink-0 rounded-md p-0"
                        title={isIncluded ? 'Remove include' : 'Include'}
                        aria-label={isIncluded ? `Remove ${item.label} from included targets` : `Include ${item.label}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleIncluded();
                        }}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={isExcluded ? 'destructive' : 'outline'}
                        className="h-7 w-7 shrink-0 rounded-md p-0"
                        title={isExcluded ? 'Remove exclude' : 'Exclude'}
                        aria-label={isExcluded ? `Remove ${item.label} from excluded targets` : `Exclude ${item.label}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleExcluded();
                        }}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function RunStep({ step }: { step: PolicyExecutionStep }) {
  const [showOutput, setShowOutput] = useState(false);
  const hasOutput = Boolean(step.output && step.output.trim());
  return (
    <li className="rounded-xl border border-border bg-card px-3 py-2.5 shadow-[0_1px_3px_rgb(15_23_42/0.04)]">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-border/70 bg-card px-1 text-[10px] font-semibold text-slate-500">
          {step.step}
        </span>
        <div className="min-w-0 flex-1">
          {step.purpose && <p className="text-[13px] font-medium text-slate-800 dark:text-slate-100">{step.purpose}</p>}
          <code className="mt-0.5 block whitespace-pre-wrap break-all font-mono text-[11.5px] leading-relaxed text-slate-600 dark:text-slate-300">
            {step.command}
          </code>
        </div>
        {hasOutput && (
          <button
            type="button"
            onClick={() => setShowOutput(value => !value)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/5"
          >
            Output
            <ChevronDown className={`h-3 w-3 transition-transform ${showOutput ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {showOutput && hasOutput && (
        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-card p-3 font-mono text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
          {step.output}
        </pre>
      )}
    </li>
  );
}

function RunCard({ run }: { run: PolicyExecutionRecord }) {
  const tone = runToneStyles[run.status] || runToneStyles.FAILED;
  const ToneIcon = tone.icon;
  const commandCount = run.attempts.reduce((total, attempt) => total + attempt.steps.length, 0);
  const meta = [
    run.startedAt ? `${formatDateTime(run.startedAt)} (${formatRelativeTime(run.startedAt)})` : '',
    triggerLabel(run),
    `${commandCount} command${commandCount === 1 ? '' : 's'}`,
    run.attempts.length > 1 ? `${run.attempts.length} attempts` : '',
  ].filter(Boolean);

  return (
    <section className={`overflow-hidden rounded-2xl border border-l-4 bg-card ${tone.border} ${CARD_SHADOW}`}>
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <h4 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">{runModeLabels[run.mode] || 'Policy run'}</h4>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            {meta.join(' · ')}
          </p>
        </div>
        <Badge variant="outline" className={`gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${tone.badge}`}>
          <ToneIcon className={`h-3.5 w-3.5 ${run.status === 'RUNNING' ? 'animate-spin' : ''}`} />
          {tone.label}
        </Badge>
      </header>
      {(run.reason || run.status === 'RUNNING') && (
        <p className={`border-t border-border px-5 py-3 text-sm leading-relaxed ${tone.text}`}>
          {run.reason || 'Whiteswan is planning and running the next commands…'}
        </p>
      )}
      {commandCount > 0 && (
        <div className="space-y-4 border-t border-border px-5 py-4">
          {run.attempts.map(attempt => (
            <div key={attempt.attempt} className="space-y-2">
              {run.attempts.length > 1 && (
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Attempt {attempt.attempt}
                  {attempt.status === 'FAILED' && attempt.reason && run.attempts.length > attempt.attempt && (
                    <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">· {attempt.reason}</span>
                  )}
                </p>
              )}
              <ol className="space-y-2">
                {attempt.steps.map(step => (
                  <RunStep key={`${attempt.attempt}-${step.step}`} step={step} />
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function PolicyDetailsEdit({
  policy,
  open,
  onClose,
  tenantName,
  cloudId,
  isCreateMode,
  onPolicyUpdate,
  onRefreshRequested,
  linkedAlertDetails,
}: PolicyDetailsEditProps) {
  const [editedPolicy, setEditedPolicy] = useState<CloudPolicy | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectableItems, setSelectableItems] = useState<SelectableItemsResponse | null>(null);
  const [isLoadingSelectable, setIsLoadingSelectable] = useState(false);
  const [availableAlerts, setAvailableAlerts] = useState<AlertOption[]>([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [alertQuery, setAlertQuery] = useState('');
  const [activeTab, setActiveTab] = useState<DetailsTab>('details');
  const [runs, setRuns] = useState<PolicyExecutionRecord[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isRecreateOpen, setIsRecreateOpen] = useState(false);
  const [recreateInstructions, setRecreateInstructions] = useState('');
  const [isRecreating, setIsRecreating] = useState(false);
  const [recreateRejection, setRecreateRejection] = useState<PolicyFeasibility | null>(null);
  const hadRunningRef = useRef(false);

  useEffect(() => {
    if (!policy) return;
    const normalized = normalizeCloudPolicy(policy);
    setEditedPolicy(normalized);
    setRuns(normalizeExecutionHistory(normalized.executionHistory));
  }, [policy]);

  useEffect(() => {
    if (!open) return;
    setActiveTab('details');
    setIsRecreateOpen(false);
    setRecreateInstructions('');
    setRecreateRejection(null);
    setAlertQuery('');
  }, [open, policy?.policyId]);

  const policyCloudId = editedPolicy?.cloudAccountId || cloudId;
  const policyTenant = editedPolicy?.tenantName || tenantName;

  useEffect(() => {
    if (!open || !editedPolicy || !policyTenant || !policyCloudId) return;
    setIsLoadingSelectable(true);
    fetchAlertSelectableItems(policyTenant, policyCloudId, editedPolicy.cloudType)
      .then(setSelectableItems)
      .catch(error => {
        console.error('Failed to fetch policy selectable items:', error);
        setSelectableItems({ users: [], resources: {} });
      })
      .finally(() => setIsLoadingSelectable(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editedPolicy?.cloudType, policyTenant, policyCloudId]);

  useEffect(() => {
    if (!open || !editedPolicy || editedPolicy.cloudPolicyType !== 'REMEDIATION' || !policyTenant || !policyCloudId) return;
    setIsLoadingAlerts(true);
    fetchAlerts(policyTenant, policyCloudId, editedPolicy.cloudType)
      .then(response => setAvailableAlerts(flattenAlerts(response)))
      .catch(error => {
        console.error('Failed to fetch alerts for policy linking:', error);
        setAvailableAlerts([]);
      })
      .finally(() => setIsLoadingAlerts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editedPolicy?.cloudType, editedPolicy?.cloudPolicyType, policyTenant, policyCloudId]);

  const loadRuns = useCallback(async () => {
    if (!editedPolicy?.policyId || !policyTenant || !policyCloudId) return;
    try {
      const latest = await fetchPolicyExecutionTimeline(editedPolicy.policyId, editedPolicy.cloudType, policyTenant, policyCloudId);
      setRuns(latest);
    } catch (error) {
      console.error('Failed to load policy runs:', error);
    } finally {
      setIsLoadingRuns(false);
    }
  }, [editedPolicy?.policyId, editedPolicy?.cloudType, policyTenant, policyCloudId]);

  useEffect(() => {
    if (!open || isCreateMode || activeTab !== 'timeline') return;
    setIsLoadingRuns(true);
    void loadRuns();
  }, [open, isCreateMode, activeTab, loadRuns]);

  const isTransitioning = editedPolicy?.policyStatus === 'ENABLING' || editedPolicy?.policyStatus === 'DISABLING';
  const hasRunningRun = runs.some(run => run.status === 'RUNNING');
  const shouldPollRuns = open && !isCreateMode && activeTab === 'timeline' && (hasRunningRun || isTransitioning);

  useEffect(() => {
    if (!shouldPollRuns) return;
    const intervalId = window.setInterval(() => void loadRuns(), RUN_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [shouldPollRuns, loadRuns]);

  useEffect(() => {
    if (hadRunningRef.current && !hasRunningRun) onRefreshRequested?.();
    hadRunningRef.current = hasRunningRun;
  }, [hasRunningRun, onRefreshRequested]);

  const linkedAlertMap = useMemo(() => {
    const map: Record<string, AlertOption> = {};
    availableAlerts.forEach(alert => {
      map[alert.id] = alert;
    });
    Object.entries(linkedAlertDetails || {}).forEach(([id, alert]: [string, any]) => {
      map[id] = {
        id,
        title: alert.title || 'Generated alert',
        severity: normalizePolicySeverity(alert.severity),
        permission: extractPermission(alert.steps),
        type: alert.alertType,
      };
    });
    return map;
  }, [availableAlerts, linkedAlertDetails]);

  const targetItems = useMemo(() => {
    const users = (selectableItems?.users || []).map(user => ({ id: user, label: user }));
    const nhis = (
      editedPolicy?.cloudType === 'AWS'
        ? selectableItems?.awsIamRole || []
        : selectableItems?.serviceAccount || selectableItems?.serviceAccounts || []
    ).map(item => ({ id: item, label: item }));
    const policyResources = filterResourcesByPolicyTargetTypes(
      selectableItems?.resources,
      editedPolicy?.policyTargetTypes,
      editedPolicy?.cloudType,
    );
    const resources = Object.entries(policyResources).flatMap(([group, values]) => (
      (values || []).map(value => ({ id: value, label: value, sublabel: group.replace(/-/g, ' ') }))
    ));
    return { users, nhis, resources };
  }, [selectableItems, editedPolicy?.cloudType, editedPolicy?.policyTargetTypes]);

  if (!editedPolicy) return null;

  const isRemediation = editedPolicy.cloudPolicyType === 'REMEDIATION';
  const severity = normalizePolicySeverity(editedPolicy.severity);
  const severityStyle = severityStyles[severity] || severityStyles.Medium;
  const excludedUsers = asStringArray(editedPolicy.excludedUsers);
  const excludedResources = asStringArray(editedPolicy.excludedResources);
  const excludedNHIs = asStringArray(editedPolicy.excludedNHIs);
  const linkedAlertIds = asStringArray(editedPolicy.linkedAlerts);
  const filteredAlerts = availableAlerts.filter(alert => (
    `${alert.title} ${alert.severity} ${alert.permission}`.toLowerCase().includes(alertQuery.toLowerCase())
  ));
  const scope = editedPolicy.policyMetadata?.scope;
  const lastFailed = !isCreateMode && editedPolicy.lastExecutionStatus === 'FAILED' && !isTransitioning;
  const recreateBlockedReason = isTransitioning
    ? 'Wait for the current run to finish.'
    : !isRemediation && editedPolicy.policyStatus === 'ACTIVE'
      ? 'Disable the policy first so its enforcement is rolled back cleanly.'
      : '';

  const setPolicyList = (key: 'includedUsers' | 'includedResources' | 'includedNHIs' | 'excludedUsers' | 'excludedResources' | 'excludedNHIs', value: string[]) => {
    setEditedPolicy(prev => ({ ...prev!, [key]: value }));
  };

  const toggleLinkedAlert = (alertId: string) => {
    setEditedPolicy(prev => ({
      ...prev!,
      linkedAlerts: toggleValue(asStringArray(prev?.linkedAlerts), alertId),
    }));
  };

  const handleSave = async () => {
    if (!editedPolicy.title.trim()) {
      toast.error('Policy title is required');
      return;
    }
    const cleanPolicy = normalizeCloudPolicy({
      ...editedPolicy,
      severity,
      linkedAlerts: isRemediation ? linkedAlertIds : [],
    });
    if (isCreateMode && onPolicyUpdate) {
      onPolicyUpdate(cleanPolicy);
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      const updated = await updatePolicy({
        ...cleanPolicy,
        policyId: cleanPolicy.policyId || cleanPolicy.id?.toString(),
        tenantName: policyTenant,
        cloudId: policyCloudId,
        executionHistory: undefined,
      });
      toast.success('Policy updated successfully');
      onPolicyUpdate?.(normalizeCloudPolicy(updated));
      onClose();
    } catch (error) {
      console.error('Failed to update policy', error);
      toast.error(getApiErrorMessage(error, 'Failed to update policy'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecreate = async () => {
    if (!editedPolicy.policyId || !policyTenant || !policyCloudId) return;
    setIsRecreating(true);
    setRecreateRejection(null);
    try {
      const result = await recreatePolicy({
        policyId: editedPolicy.policyId,
        cloudType: editedPolicy.cloudType,
        tenantName: policyTenant,
        cloudId: policyCloudId,
        instructions: recreateInstructions.trim() || undefined,
      });
      const recreated = result.policy[0];
      if (!recreated || result.feasibility?.feasible === false) {
        setRecreateRejection(result.feasibility || { feasible: false, reason: 'The policy could not be recreated. Try adding instructions.' });
        return;
      }
      const normalized = normalizeCloudPolicy(recreated);
      setEditedPolicy(normalized);
      onPolicyUpdate?.(normalized);
      setIsRecreateOpen(false);
      setRecreateInstructions('');
      toast.success('Policy recreated', { description: 'Review the new plan in Policy Data, then run it.' });
    } catch (error) {
      console.error('Failed to recreate policy', error);
      toast.error(getApiErrorMessage(error, 'Failed to recreate the policy'));
    } finally {
      setIsRecreating(false);
    }
  };

  const tabs: { key: DetailsTab; label: string; icon: any }[] = [
    { key: 'details', label: 'Policy Details', icon: Sparkles },
    ...(!isCreateMode ? [{ key: 'timeline' as const, label: 'Execution History', icon: Activity }] : []),
    { key: 'data', label: 'Policy Data', icon: ShieldCheck },
  ];

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="policy-details-dialog flex h-[92vh] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 overflow-hidden rounded-[18px] border-border/60 bg-card p-0 shadow-[0_24px_80px_rgb(15_23_42/0.18)]">
        <DialogHeader className="border-b border-border bg-card px-6 py-5">
          <div className="flex flex-wrap items-center justify-center gap-2 pr-8">
            <Badge variant="outline" className="rounded-md bg-sky-50 text-sky-700 border-sky-200">
              <Cloud className="mr-1 h-3 w-3" />
              {editedPolicy.cloudType}
            </Badge>
            <Badge variant="outline" className={severityStyle.badge}>
              <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${severityStyle.dot}`} />
              {severity}
            </Badge>
            <Badge variant="outline" className={isRemediation ? 'rounded-md bg-violet-50 text-violet-700 border-violet-200' : 'rounded-md bg-rose-50 text-rose-700 border-rose-200'}>
              {isRemediation ? <Wrench className="mr-1 h-3 w-3" /> : <ShieldCheck className="mr-1 h-3 w-3" />}
              {isRemediation ? 'Remediation' : 'Guardrail'}
            </Badge>
            {(editedPolicy.policyTargetTypes || []).map((type, index) => (
              <Badge key={index} variant="outline" className="rounded-md bg-card text-slate-700 border-border">
                {type}
              </Badge>
            ))}
            {scope && (typeof scope.entities === 'number' || typeof scope.commands === 'number') && (
              <Badge variant="outline" className="rounded-md bg-card text-slate-600 border-border" title="Estimated size of one run">
                {scope.entities ?? 0} entities · ~{scope.commands ?? 0} commands
              </Badge>
            )}
          </div>
          <DialogTitle className="sr-only">{editedPolicy.title || 'Policy details'}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-center gap-1 border-b border-border bg-card px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? 'text-primary' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.key === 'timeline' && (hasRunningRun || isTransitioning) && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                )}
                {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-background/40">
          <div className="mx-auto w-full max-w-full space-y-5 p-6 pb-8">
            {isRecreateOpen && (
              <section className={`rounded-2xl border border-primary/25 bg-card p-5 ${CARD_SHADOW}`}>
                <div className="flex items-start justify-between gap-3">
                  <SectionHeading
                    icon={RefreshCw}
                    accent="text-primary bg-primary/5 border-primary/20"
                    title="Recreate policy"
                    subtitle="Regenerates the targets, command plan and rollback from this policy's intent. Unsaved edits are discarded."
                  />
                  <button
                    type="button"
                    onClick={() => setIsRecreateOpen(false)}
                    className="rounded-md p-1 text-slate-400 hover:text-slate-700"
                    aria-label="Close recreate"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 space-y-2">
                  <Label htmlFor="recreate-instructions">Instructions (optional)</Label>
                  <Textarea
                    id="recreate-instructions"
                    value={recreateInstructions}
                    onChange={event => setRecreateInstructions(event.target.value)}
                    placeholder="For example: only cover the prod buckets, or use an IAM group instead of individual users."
                    className="min-h-20 resize-none rounded-md border-border/70 bg-card"
                  />
                </div>
                {recreateRejection && (
                  <div className="mt-4 rounded-xl border border-red-500/25 bg-card p-4 shadow-[0_2px_8px_rgb(239_68_68/0.08)]">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-600">
                      <OctagonAlert className="h-4 w-4" />
                      Can&apos;t recreate as described
                    </p>
                    <p className="mt-1 text-sm text-slate-600">{recreateRejection.reason}</p>
                    {recreateRejection.suggestedPrompt && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <p className="text-sm text-slate-700">Try: <span className="font-medium">{recreateRejection.suggestedPrompt}</span></p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-md text-xs"
                          onClick={() => {
                            setRecreateInstructions(recreateRejection.suggestedPrompt || '');
                            setRecreateRejection(null);
                          }}
                        >
                          Use as instructions
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setIsRecreateOpen(false)} disabled={isRecreating}>
                    Cancel
                  </Button>
                  <Button onClick={handleRecreate} disabled={isRecreating} className="rounded-md">
                    {isRecreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    {isRecreating ? 'Recreating…' : 'Recreate'}
                  </Button>
                </div>
              </section>
            )}

            {activeTab === 'details' && (
              <>
                {lastFailed && (
                  <section className={`flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-l-4 border-red-500/25 border-l-red-500 bg-card p-4 ${CARD_SHADOW}`}>
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
                        <OctagonAlert className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-red-700">
                          Last run failed{editedPolicy.lastExecutedAt ? ` ${formatRelativeTime(editedPolicy.lastExecutedAt)}` : ''}
                        </p>
                        <p className="mt-0.5 text-sm text-slate-600">{editedPolicy.lastExecutionReason || 'The policy could not be applied.'}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setActiveTab('timeline')}>
                        View run
                      </Button>
                      {!recreateBlockedReason && (
                        <Button size="sm" className="h-8 rounded-md" onClick={() => setIsRecreateOpen(true)}>
                          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                          Recreate
                        </Button>
                      )}
                    </div>
                  </section>
                )}

                <section className={`rounded-2xl border border-border bg-card p-5 ${CARD_SHADOW}`}>
                  <div className="mb-4 flex flex-col gap-3">
                    <SectionHeading
                      icon={Sparkles}
                      accent="text-primary bg-primary/5 border-primary/20"
                      title="Policy Definition"
                      subtitle="Edit the visible policy metadata before saving."
                    />
                    <div className="pl-10">
                      <SeverityEditor
                        value={severity}
                        onChange={(value: string) => setEditedPolicy(prev => ({ ...prev!, severity: normalizePolicySeverity(value) }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={editedPolicy.title || ''}
                        onChange={event => setEditedPolicy(prev => ({ ...prev!, title: event.target.value }))}
                        className="h-11 rounded-md border-border/70 bg-card text-base font-medium"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={editedPolicy.description || ''}
                        onChange={event => setEditedPolicy(prev => ({ ...prev!, description: event.target.value }))}
                        className="min-h-28 resize-none rounded-md border-border/70 bg-card leading-6"
                      />
                    </div>
                  </div>
                </section>

                {isRemediation && (
                  <section className={`w-full max-w-full overflow-hidden rounded-2xl border border-border bg-card p-4 ${CARD_SHADOW}`}>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <SectionHeading
                        icon={Link2}
                        accent="text-violet-600 bg-violet-50 border-violet-200"
                        title="Linked Alerts"
                        subtitle="The policy runs as soon as one of these alerts fires."
                      />
                      <Badge variant="outline" className="rounded-md bg-card">{linkedAlertIds.length} linked</Badge>
                    </div>

                    <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                      {linkedAlertIds.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border bg-card p-4 text-sm text-slate-500 md:col-span-2">
                          No alerts linked yet. Pick at least one below.
                        </div>
                      ) : linkedAlertIds.map(alertId => {
                        const alert = linkedAlertMap[alertId];
                        const isMissing = !alert && !isLoadingAlerts;
                        const alertSeverity = normalizePolicySeverity(alert?.severity);
                        const alertSeverityStyle = severityStyles[alertSeverity] || severityStyles.Medium;
                        return (
                          <div
                            key={alertId}
                            className={`relative max-w-full overflow-hidden rounded-2xl border border-l-4 border-border bg-card p-3 shadow-[0_1px_3px_rgb(15_23_42/0.04)] ${
                              isMissing ? 'border-l-slate-300' : alertSeverityStyle.panel
                            }`}
                          >
                            <div className="flex min-w-0 items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                                  {alert?.title || (isLoadingAlerts ? 'Loading alert…' : 'Alert no longer available')}
                                </p>
                                <p className="mt-1 truncate text-xs text-slate-500">
                                  {alert ? (alert.permission || alert.type || 'Linked alert') : isMissing ? 'It may have been deleted. Remove it or link another alert.' : ''}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                {alert && (
                                  <Badge variant="outline" className={`${alertSeverityStyle.badge} rounded-md text-[10px]`}>
                                    {alertSeverity}
                                  </Badge>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleLinkedAlert(alertId)}
                                  className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                  aria-label="Unlink alert"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={alertQuery}
                        onChange={event => setAlertQuery(event.target.value)}
                        placeholder="Search alerts by title, severity, or permission..."
                        className="h-10 rounded-md border-border/70 bg-card pl-9"
                      />
                    </div>

                    <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                      {isLoadingAlerts ? (
                        <div className="rounded-2xl border border-border bg-card p-4 text-center text-sm text-slate-500">
                          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin text-primary" />
                          Loading alerts...
                        </div>
                      ) : filteredAlerts.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border bg-card p-4 text-sm text-slate-500">
                          No matching alerts found.
                        </div>
                      ) : filteredAlerts.map(alert => {
                        const selected = linkedAlertIds.includes(alert.id);
                        const alertSeverityStyle = severityStyles[normalizePolicySeverity(alert.severity)] || severityStyles.Medium;
                        return (
                          <button
                            type="button"
                            key={alert.id}
                            onClick={() => toggleLinkedAlert(alert.id)}
                            className={`w-full rounded-2xl border p-3 text-left shadow-[0_1px_3px_rgb(15_23_42/0.04)] transition-colors ${
                              selected ? 'border-primary bg-primary/5' : 'border-border bg-card portal-card-hover'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{alert.title}</p>
                                {alert.permission && <p className="mt-1 truncate text-xs text-slate-500">{alert.permission}</p>}
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <Badge variant="outline" className={`${alertSeverityStyle.badge} rounded-md text-[10px]`}>
                                  {alert.severity}
                                </Badge>
                                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md border ${
                                  selected ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-white'
                                }`}>
                                  {selected && <Check className="h-3 w-3" />}
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                <div className="flex flex-col gap-5">
                  {visibleTargets.map(kind => {
                    const config = targetConfig[kind];
                    if (isLoadingSelectable) {
                      return (
                        <section key={kind} className={`flex flex-col rounded-2xl border border-border bg-card p-4 ${CARD_SHADOW}`}>
                          <SectionHeading icon={config.icon} accent={config.accent} title={config.title} subtitle="Loading selectable targets..." />
                          <div className="mt-4 flex items-center justify-center py-6 text-sm text-slate-500">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin text-primary" />
                            Loading {config.title.toLowerCase()}...
                          </div>
                        </section>
                      );
                    }
                    return (
                      <TargetPicker
                        key={kind}
                        kind={kind}
                        items={targetItems[kind]}
                        included={kind === 'users' ? editedPolicy.includedUsers || [] : kind === 'nhis' ? editedPolicy.includedNHIs || [] : editedPolicy.includedResources || []}
                        excluded={kind === 'users' ? excludedUsers : kind === 'nhis' ? excludedNHIs : excludedResources}
                        onIncludedChange={(next) => {
                          if (kind === 'users') setPolicyList('includedUsers', next);
                          if (kind === 'nhis') setPolicyList('includedNHIs', next);
                          if (kind === 'resources') setPolicyList('includedResources', next);
                        }}
                        onExcludedChange={(next) => {
                          if (kind === 'users') setPolicyList('excludedUsers', next);
                          if (kind === 'nhis') setPolicyList('excludedNHIs', next);
                          if (kind === 'resources') setPolicyList('excludedResources', next);
                        }}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {activeTab === 'timeline' && (
              isLoadingRuns && runs.length === 0 ? (
                <div className={`rounded-2xl border border-border bg-card p-8 text-center text-sm text-slate-500 ${CARD_SHADOW}`}>
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-primary" />
                  Loading runs...
                </div>
              ) : runs.length === 0 ? (
                <div className={`rounded-2xl border border-dashed border-border bg-card p-8 text-center ${CARD_SHADOW}`}>
                  <Activity className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium text-slate-500">No runs yet</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {isRemediation
                      ? 'Runs appear here each time a linked alert fires while the policy is armed.'
                      : 'Runs appear here once the policy is executed.'}
                  </p>
                </div>
              ) : (
                runs.map(run => <RunCard key={run.id} run={run} />)
              )
            )}

            {activeTab === 'data' && (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {[
                  { key: 'enable', title: 'Policy Enable Data', subtitle: 'Reference plan the executor follows.', icon: ShieldCheck, accent: 'text-emerald-600 bg-emerald-50 border-emerald-200', data: editedPolicy.policyEnableData },
                  { key: 'disable', title: 'Policy Disable Data', subtitle: 'Rollback plan used when the policy is disabled.', icon: ShieldOff, accent: 'text-rose-600 bg-rose-50 border-rose-200', data: editedPolicy.policyDisableData },
                ].map(section => (
                  <section key={section.key} className={`min-w-0 rounded-2xl border border-border bg-card p-4 ${CARD_SHADOW}`}>
                    <div className="mb-3">
                      <SectionHeading icon={section.icon} accent={section.accent} title={section.title} subtitle={section.subtitle} />
                    </div>
                    <div className="h-[65vh] overflow-auto rounded-2xl border border-border bg-card p-4">
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700 dark:text-slate-300">
                        {section.data && Object.keys(section.data).length > 0
                          ? JSON.stringify(section.data, null, 2)
                          : 'No data available'}
                      </pre>
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center border-t border-border bg-card p-4 sm:justify-between">
          <div>
            {!isCreateMode && (
              <Button
                variant="outline"
                className="rounded-md"
                onClick={() => {
                  setIsRecreateOpen(true);
                  setActiveTab('details');
                }}
                disabled={Boolean(recreateBlockedReason) || isRecreateOpen}
                title={recreateBlockedReason || 'Regenerate this policy from its intent'}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Recreate policy
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button className="rounded-md px-6" onClick={handleSave} disabled={isSaving || isRecreating}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {isCreateMode ? 'Update Selection' : 'Save Changes'}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
